import React, { useEffect } from 'react';
import { useTranslations } from 'hooks/useTranslations';
import { colors } from 'theme/colors';
import Grid from '@mui/material/Grid';
import { Typography } from '@mui/material';
import { Chips } from 'components/atoms/Chips';
import Button from 'components/atoms/Button';
import { EButtonVariants } from 'constants/Buttons';
import { Link, useNavigate } from 'react-router-dom';
import { ERouteLinks } from 'models/route';
import { IDictionary } from 'models/dictionary';
import { IAccessory, IAncillary } from 'models/ancillaries';
import { findInDictionary, parseDictionary } from 'helpers/dictionary';
import { LEGAL_ID } from 'pages/Firearms/RegisteredFirearmsMapPage/FirearmsLocationMap';
import { renderDataColorByValues } from 'components/molecules/Table/helpers';
import { EPermission } from 'models/permissions';
import { usePermission } from 'hooks/usePermission';
import { emptyFieldHolder } from 'components/molecules/CardList';
import { useLang } from 'models/langContext';
import { useDictionary } from 'models/dictionaryContext';

interface IAncillaryDataListItem {
  data: IAccessory | IAncillary;
  state: IDictionary[] | null;
}

const AccessoryDataListItem = ({ data, state }: IAncillaryDataListItem) => {
  const { t } = useTranslations();
  const navigate = useNavigate();
  const { hasPermissions } = usePermission();
  const isLegal = data.legalityUniqueId === LEGAL_ID;
  const { selectedLanguage } = useLang();
  const {
    getArtAccessoryType,
    artAccessoryType,
    getArtAccessoryHostType,
    artAccessoryHostType,
  } = useDictionary();

  useEffect(() => {
    (async function initDictionaries() {
      getArtAccessoryType();
      getArtAccessoryHostType();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLanguage]);

  return (
    <Grid
      container
      sx={{
        border: `1px solid ${colors.gray200}`,
        borderRadius: '4px',
        overflow: 'hidden',
        mt: 1,
        backgroundColor: `${isLegal ? colors.gray100 : colors.error15}`,
      }}
    >
      <Grid size={{ xs: 6 }} sx={{ p: 2 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t('ancillaries.ancillariesLocation.accessoriesDetails.section.serialNumber.label')}
        </Typography>
        <Typography variant="body2">{emptyFieldHolder(data.serialNumber)}</Typography>
      </Grid>

      <Grid size={{ xs: 6 }} sx={{ p: 2 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t('ancillaries.ancillariesLocation.accessoriesDetails.section.firecycleId.label')}
        </Typography>
        <Typography variant="body2">{emptyFieldHolder(data.fireCycleId)}</Typography>
      </Grid>

      <Grid size={{ xs: 6 }} sx={{ p: 2 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t('ancillaries.ancillariesLocation.accessoriesDetails.section.accessoryType.label')}
        </Typography>
        <Typography variant="body2">
          {emptyFieldHolder(findInDictionary(data.typeUniqueId, artAccessoryType))}
        </Typography>
      </Grid>

      <Grid size={{ xs: 6 }} sx={{ p: 2 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t('ancillaries.ancillariesLocation.accessoriesDetails.section.hostType.label')}
        </Typography>
        <Typography variant="body2">
          {emptyFieldHolder(findInDictionary(data.hostTypeUniqueId, artAccessoryHostType))}
        </Typography>
      </Grid>

      <Grid size={{ xs: 6 }} sx={{ p: 2 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t('ancillaries.ancillariesLocation.accessoriesDetails.section.state.label')}
        </Typography>
        <Chips
          text={findInDictionary(data.stateUniqueId, state)}
          variant={
            renderDataColorByValues(
              data.activityUniqueId,
              data.legalityUniqueId,
              data.stateUniqueId,
            ).stateField
          }
        />
      </Grid>

      {hasPermissions(
        [EPermission.VIEW_LIMITED_ANCILLARIES, EPermission.VIEW_ALL_ANCILLARIES],
        true,
      ) && (
        <Grid
          size={{ xs: 12 }}
          sx={{
            margin: '0 10px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderTop: {
              xs: `1px solid ${colors.gray200}`,
            },
            p: 2,
          }}
        >
          <Grid size={{ xs: 12 }}>
            <Link
              to={ERouteLinks.ViewAccessories.replace(':id', data.uniqueId)}
              target="_blank"
            >
              <Button
                label={t('registeredFirearms.firearmsLocation.seeDetails.label')}
                variant={EButtonVariants.outlined}
                size="small"
                fullWidth
              />
            </Link>
          </Grid>
        </Grid>
      )}
    </Grid>
  );
};

export default AccessoryDataListItem;
